/**
 * سیگنال‌های «عملکرد من» (سنجه، semantic-search growth-opportunities v2) که با ساعت کاری و مرخصی
 * درست می‌شوند. همان پاسخی که مسیر رشد می‌خواند؛ وضعیت و عدد را سرور با گاردهای خودش ساخته
 * (مثلاً کمینه‌ی ۱۰ گزارش برای زمان انتظار آنلاین)، این‌جا فقط خوانده می‌شود.
 */

export type SanjeIndicator = {
  key?: string;
  status?: string;
  title?: string;
  value?: number;
  score_impact?: number | null;
  ranking_impact?: number;
  band?: { id?: string } | null;
};

export type SanjeSignal =
  | { kind: 'inperson_absence'; reports: number | null; affectsRanking: boolean }
  | { kind: 'online_absence'; reports: number | null }
  | { kind: 'booking_calendar' }
  | { kind: 'in_person_waiting'; minutes: number }
  | { kind: 'online_waiting'; minutes: number }
  | { kind: 'delay_followups'; title: string };

/** راوی هر گزارش «ویزیت ناموفق» ۳۰ روز اخیر را −۰٫۱ می‌نویسد (۳ گزارش = −۰٫۳). */
const PER_REPORT_PENALTY = 0.1;

const reportsFromPenalty = (impact: number | null | undefined) => {
  if (impact == null || !Number.isFinite(impact) || impact >= 0) return null;
  const count = Math.round(Math.abs(impact) / PER_REPORT_PENALTY);
  return count > 0 ? count : null;
};

export const extractSanjeIndicators = (payload: unknown): SanjeIndicator[] => {
  const list = (payload as { indicators?: unknown } | null)?.indicators;
  return Array.isArray(list) ? (list.filter(item => item && typeof item === 'object') as SanjeIndicator[]) : [];
};

export const pickSanjeSignals = (indicators: SanjeIndicator[]): SanjeSignal[] => {
  const byKey = new Map(indicators.filter(item => item.key).map(item => [item.key!, item]));
  const flagged = (key: string) => {
    const item = byKey.get(key);
    return item && item.status === 'improve' ? item : undefined;
  };
  const signals: SanjeSignal[] = [];

  const inperson = flagged('inperson_absence');
  if (inperson) {
    signals.push({
      kind: 'inperson_absence',
      reports: reportsFromPenalty(inperson.score_impact),
      affectsRanking: (inperson.ranking_impact ?? inperson.score_impact ?? 0) < 0,
    });
  }
  const online = flagged('online_absence');
  if (online) signals.push({ kind: 'online_absence', reports: reportsFromPenalty(online.score_impact) });
  if (flagged('booking_calendar')) signals.push({ kind: 'booking_calendar' });

  const inPersonWaiting = flagged('in_person_waiting');
  if (inPersonWaiting && Number.isFinite(inPersonWaiting.value)) {
    signals.push({ kind: 'in_person_waiting', minutes: Math.round(inPersonWaiting.value!) });
  }
  const onlineWaiting = flagged('online_waiting');
  if (onlineWaiting && Number.isFinite(onlineWaiting.value)) {
    signals.push({ kind: 'online_waiting', minutes: Math.round(onlineWaiting.value!) });
  }
  const delay = flagged('delay_penalty');
  if (delay?.title) signals.push({ kind: 'delay_followups', title: delay.title });

  return signals;
};
